import { useState, useMemo, useEffect } from 'react'

import {
	EuiBasicTable,
	EuiFlexGroup,
	EuiFlexItem,
	EuiPage,
	EuiPageBody,
	EuiPageSection,
	EuiPanel,
} from '@equipmentshare/ds2'

import BasicActions from 'views/components/BasicActions'
import InventoryLocationsSubHeader from 'views/components/InventoryLocationsSubHeader'
import PageTitle from 'views/components/PageTitle/PageTitle'
import RefreshButton from 'views/components/RefreshButton'
import SearchBar from 'views/components/SearchBar'
import InventoryLocationsFilter from 'views/components/InventoryLocationsFilter'
import StyledHeader from 'views/components/StyledHeader'
import CursorPagination from 'views/components/CursorPagination'

import { useManualAdjustmentsQuery, ManualAdjustmentsQuery } from '../../../../graphql'
import useStoreIds from 'hooks/useStoreIds'
import { TABLE_HEIGHT } from 'views/helpers/utils'
import { heapEvent } from '@equipmentshare/heaper'
import useReasonFilter from './hooks/useReasonFilter'
import useDateRangeFilter from './hooks/useDateRangeFilter'

import getColumns from './columns'

// Extract the actual ManualAdjustment type returned by the query
type ManualAdjustmentNode = ManualAdjustmentsQuery['manualAdjustments']['edges'][number]['node']

export default function InventoryAdjustmentsView() {
	const { inventoryLocationIds } = useStoreIds()
	const [ search, setSearch ] = useState( '' )
	const [ rowSize, setRowSize ] = useState( 20 )

	const columns = useMemo( () => getColumns( inventoryLocationIds.length !== 1 ), [ inventoryLocationIds ] )

	const {
		render: renderReasonFilter,
		filterVariables: reasonFilterVariables,
	} = useReasonFilter()

	const {
		render: renderDateRangeFilter,
		filterVariables: dateRangeFilterVariables,
	} = useDateRangeFilter()

	const { data, loading, refetch } = useManualAdjustmentsQuery( {
		variables: {
			inventoryLocations: inventoryLocationIds,
			search,
			...reasonFilterVariables,
			...dateRangeFilterVariables,
			paging: {
				first: rowSize,
			}
		},
	} )

	const getNextPage = ( cursor: string ) => () => {
		refetch( { paging: { first: rowSize, after: cursor } } )
	}

	const getPreviousPage = ( cursor: string ) => () => {
		refetch( { paging: { last: rowSize, before: cursor } } )
	}

	const manualAdjustments = useMemo( () => {
		if ( data?.manualAdjustments?.edges ) {
			return data.manualAdjustments.edges.map( ( edge ) => edge.node )
		}
		return []
	}, [ data ] )

	useEffect( () => {
		// when search or filters change, reset pagination
		refetch( { paging: { first: rowSize } } )
	}, [ inventoryLocationIds, search, reasonFilterVariables, dateRangeFilterVariables ] )

	const onSearchChange = ( s: string ) => {
		heapEvent( 'Inventory - Inventory Adjustments - Change - Search Input', { search_term: s } )
		setSearch( s )
	}

	const getNoItemsMessage = () => {
		if ( loading ) {
			return 'Loading data'
		}
		if ( !inventoryLocationIds.length ) {
			return 'Select a store'
		}

		return 'No records found'
	}

	const fullHeight = 'eui-fullHeight'

	return (
		<EuiPage paddingSize="l">
		  <EuiPageBody className={fullHeight}>
				<PageTitle title="Inventory Adjustments" />

				<StyledHeader>
					<EuiFlexGroup wrap>
						<EuiFlexItem style={{ minWidth: 677 }}>
							<div className="content searchFilter">
								<SearchBar
									search={search}
									setSearch={onSearchChange}
								/>
								<InventoryLocationsFilter />
								{renderReasonFilter()}
								{renderDateRangeFilter()}
								<RefreshButton refetchFunction={refetch} />
							</div>
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<div className="content actions">
								<BasicActions />
							</div>
						</EuiFlexItem>
					</EuiFlexGroup>
				</StyledHeader>
				<InventoryLocationsSubHeader pageTitle="Inventory" />

				<EuiPageSection className={fullHeight} paddingSize="none">
					<EuiPanel paddingSize="none" className="eui-yScroll" hasShadow={false} style={{ height: TABLE_HEIGHT }}>
						<EuiBasicTable
							loading={loading}
							noItemsMessage={getNoItemsMessage()}
							columns={columns}
							items={manualAdjustments}
						/>
					</EuiPanel>
					{ !!data?.manualAdjustments.pageInfo && (
						<CursorPagination
							pageInfo={data.manualAdjustments.pageInfo}
							pageForward={getNextPage}
							pageBackward={getPreviousPage}
							rowSize={rowSize}
							setRowSize={setRowSize}
						/>
					) }
				</EuiPageSection>
			</EuiPageBody>
		</EuiPage>
	)
}
